import { IS_AWS_MODE } from '../config/frontend';
import { getRequestAuthToken } from '../auth/appliedAuthToken';
import { getSharedAuthRevision } from '../auth/sharedAuthState';

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export const authHeaders = (): Record<string, string> => {
  // AWS mode uses the fail-closed tab-applied token; non-AWS keeps legacy
  // localStorage behaviour. Every authenticated request boundary: a request must
  // never be authorized with a shared token this tab has not adopted yet, never
  // carry `Bearer null`, and never silently omit Authorization in AWS mode.
  const token = getRequestAuthToken();
  if (IS_AWS_MODE && !token) {
    throw new ApiError('unauthenticated', 'Authentication required', 401);
  }
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// C21-R3-N1: Structured API error — semantic code for UI, detail for debugging.
export class ApiError extends Error {
  semanticCode: string;
  /** @deprecated Use semanticCode instead. Preserved for backward compat. */
  code: string;
  httpStatus?: number;
  technicalCause?: string;
  detail: string;
  constructor(semanticCode: string, detail: string, httpStatus?: number, technicalCause?: string) {
    super(detail);
    this.name = 'ApiError';
    this.semanticCode = semanticCode;
    this.detail = detail;
    this.httpStatus = httpStatus;
    this.technicalCause = technicalCause;
    // Preserve legacy .code for backward compat
    this.code = semanticCode;
  }
}

/**
 * C21-R4-N4: Status-aware semantic IoT API error classification.
 *
 * Priority order:
 *   1. Structured backend semantic code (if present in response body)
 *   2. HTTP status code → semantic mapping
 *   3. Shape of detail (FastAPI validation array vs. generic object)
 *   4. Network error
 *   5. Fallback unknown_error
 */
function classifyError(status: number, detail: unknown): { semanticCode: string; technicalCause?: string } {
  // 1. If detail is an object with a semantic code from the backend, use it
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const obj = detail as Record<string, unknown>;
    if (typeof obj.code === 'string' && obj.code.length > 0) {
      return { semanticCode: obj.code, technicalCause: typeof obj.msg === 'string' ? obj.msg : undefined };
    }
  }

  // 2. HTTP status → semantic mapping
  switch (status) {
    case 400:
      return { semanticCode: 'bad_request' };
    case 401:
      return { semanticCode: 'unauthenticated' };
    case 403:
      return { semanticCode: 'forbidden' };
    case 404:
      return { semanticCode: 'not_found' };
    case 409:
      return { semanticCode: 'conflict' };
    case 422: {
      // FastAPI validation: array of { loc, msg, type }
      if (Array.isArray(detail)) {
        return { semanticCode: 'validation_error', technicalCause: JSON.stringify(detail) };
      }
      return { semanticCode: 'validation_error' };
    }
    case 429:
      return { semanticCode: 'rate_limited' };
    default:
      break;
  }

  if (status >= 500 && status < 600) {
    return { semanticCode: 'server_error' };
  }

  // 3. Shape-based: non-422 with object/array detail — don't assume validation_error
  // Already handled above; fall through to unknown

  // 5. Fallback
  return { semanticCode: 'unknown_error' };
}

async function handle(res: Response) {
  if (!res.ok) {
    let detail: unknown = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      detail = data.detail ?? detail;
    } catch {
      /* ignore — keep HTTP status text */
    }

    const { semanticCode, technicalCause } = classifyError(res.status, detail);

    // Build a safe detail string for debugging (never render directly in UI)
    let safeDetail: string;
    if (typeof detail === 'string') {
      safeDetail = detail;
    } else if (Array.isArray(detail)) {
      // FastAPI validation array — extract first message for debugging
      const first = detail[0];
      safeDetail = first && typeof first.msg === 'string' ? first.msg : JSON.stringify(detail);
    } else if (detail && typeof detail === 'object') {
      const obj = detail as Record<string, unknown>;
      safeDetail = typeof obj.msg === 'string' ? obj.msg
        : typeof obj.message === 'string' ? obj.message
        : JSON.stringify(detail);
    } else {
      safeDetail = String(detail);
    }

    throw new ApiError(semanticCode, safeDetail, res.status, technicalCause);
  }
  return res.json();
}

/**
 * C21-R5-N5: Common fetch wrapper that converts pre-response rejections
 * (offline, DNS, CORS, connection refused, timeout, abort) into stable
 * ApiError instances.  All iotApi methods route through this function so
 * consumers never receive raw TypeError / DOMException.
 */
async function safeFetch(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (err: unknown) {
    // Distinguish AbortError (timeout / manual abort) from network errors.
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(
        'request_aborted',
        'Request was aborted',
        undefined,
        err.message,
      );
    }
    // Any other fetch rejection → network_error.
    // technicalCause captures the native message for internal logs only.
    const message = err instanceof Error ? err.message : String(err);
    throw new ApiError('network_error', 'Network request failed', undefined, message);
  }
}

// Retry only safe reads, once. Never replay physical commands or writes.
async function readJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const headers = authHeaders();
  const authRevision = getSharedAuthRevision();
  const sameSession = () => getSharedAuthRevision() === authRevision
    && JSON.stringify(authHeaders()) === JSON.stringify(headers);
  const url = `${BASE}/api${path}`;
  // Command-read failures must immediately invalidate control readiness.
  const canRetry = !/^\/iot\/commands(?:[/?]|$)/.test(path);
  for (let attempt = 0; ; attempt += 1) {
    let response: Response;
    try {
      response = await safeFetch(url, { headers, ...(signal ? { signal } : {}) });
    } catch (error) {
      if (!canRetry || attempt !== 0 || !(error instanceof ApiError) || error.semanticCode !== 'network_error') throw error;
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!sameSession()) throw error;
      continue;
    }
    if (canRetry && attempt === 0 && [502, 503, 504].includes(response.status)) {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!sameSession()) return handle(response) as Promise<T>;
      continue;
    }
    return handle(response) as Promise<T>;
  }
}

export const iotApi = {
  get: async (path: string) =>
    readJson<any>(path),
  getJson: async <T>(path: string, signal?: AbortSignal) =>
    readJson<T>(path, signal),
  post: async (
    path: string,
    body?: unknown,
    idempotencyKey?: string,
    extraHeaders?: Record<string, string>,
  ) =>
    safeFetch(`${BASE}/api${path}`, {
      method: 'POST',
      // Narrow header seam: only Idempotency-Key and explicit caller-supplied
      // business headers (e.g. X-AgriSafe-Expected-Node-ID) may be added;
      // Authorization + Content-Type remain controlled by authHeaders().
      headers: {
        ...authHeaders(),
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
        ...(extraHeaders ?? {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    }).then(handle),
  put: async (path: string, body?: unknown) =>
    safeFetch(`${BASE}/api${path}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    }).then(handle),
  del: async (path: string) =>
    safeFetch(`${BASE}/api${path}`, { method: 'DELETE', headers: authHeaders() }).then(handle),
  // Authenticated file download (CSV/PDF) — fetch as blob then trigger save.
  download: async (path: string, fallbackName: string) => {
    const res = await safeFetch(`${BASE}/api${path}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const match = cd.match(/filename="?([^"]+)"?/);
    const name = match ? match[1] : fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// ---- Types ----
export interface CanonicalMeasurement {
  equipment_template_id: string;
  equipment_template_version: string;
  channel_id: string;
  measurement_id: string;
  measurement_version: string;
  unit_id: string;
  unit_version: string;
  value: unknown;
  source_field: string;
}

export interface NodeLatest {
  measures?: {
    schema?: string;
    canonical_measurements?: CanonicalMeasurement[];
    battery_v?: number;
    valve_state?: number;
    flow_velocity_lpm?: number;
    flow_l_min?: number;
    flow_lpm?: number;
    pressure_bar?: number;
    pressure?: number;
    soil_moisture_pct?: number;
    soil_moisture?: number;
    moisture_pct?: number;
    soil_ec_us_cm?: number;
    soil_temperature_c?: number;
    air_temperature_c?: number;
    air_humidity_pct?: number;
    pump_state?: number;
    /**
     * @deprecated Never emitted by the backend. The AGLW4R01 codec produces
     * per-channel `relay_N_state` fields (below); a single scalar `relay_state`
     * has no physical meaning on a 4-channel module and must never be read.
     */
    relay_state?: number;
    /**
     * AGLW4R01 4-channel relay contact states (0 = OFF, 1 = ON), keyed K1..K4.
     *
     * Present in TELEMETRY HISTORY `measures` (pipeline.py persists the decoded
     * dict verbatim). On a LIVE node these same fields live at the node ROOT,
     * not here — see `IoTNode` below. Both shapes are read through
     * `getRelayChannels` / `getNodeRelayChannels` so no caller reimplements the
     * root-vs-measures precedence.
     *
     * A relay contact is NOT proof the motor shaft is turning (section 8).
     */
    relay_1_state?: number;
    relay_2_state?: number;
    relay_3_state?: number;
    relay_4_state?: number;
    motor_state?: number;
    current_a?: number;
    water_level_m?: number;
    ultrasonic_range_mm?: number;
    flow_rate_l?: number;
    valve_on_seconds?: number;
    reporting_interval_s?: number;
    packet_seq?: number;
  };
  ts?: string;
}

export interface IoTNode {
  parcel_supervision?: Record<string, import('./parcelSupervision').ParcelSupervisionData>;
  environment_summary?: { temperatureC: number | null; humidityPercent: number | null; observedAt: number | null; serverTime: number; validUntil: number | null };
  irrigation_summary?: (import('./pressureDuration').PressureDuration & {
    observedAt: number; freshnessMs: number; serverTime: number;
  }) | null;
  id: string;
  name: string;
  model: string;
  dev_eui: string;
  ttn_device_id?: string;
  valve_state?: number | null;
  battery_v?: number | null;
  flow_velocity_lpm?: number | null;
  /**
   * Live AGLW4R01 relay contact states, written to the node ROOT by the
   * ingestion pipeline (`pipeline.py` / `ttn_webhook.py` legacy live-state),
   * exactly like `valve_state` / `battery_v`. `relay_4ch` is a legacy-runtime
   * model: it produces NO `canonical_measurements`, so these root fields are
   * the only live source of relay truth.
   */
  relay_1_state?: number | null;
  relay_2_state?: number | null;
  relay_3_state?: number | null;
  relay_4_state?: number | null;
  last_seen_at?: string | null;
  latest?: NodeLatest | null;
  sector_id?: string | null;
  sector_name?: string | null;
  /**
   * FIELD pump binding (PR #105). The UI consumes only the business-safe
   * fields it needs — never the internal relay/channel/hydraulic layout.
   */
  pump_control?: {
    pump_id?: string;
    status?: string;
  };
}

export interface Command {
  id: string;
  /** FIELD batch command-safety read returns ``command_id`` (legacy/demo ledger uses ``id``). */
  command_id?: string;
  node_id: string;
  type: string;
  channel_id?: string | null;
  params: Record<string, unknown>;
  f_port: number;
  payload_hex: string;
  status: string;
  requested_by: string;
  requested_at: string;
  sent_at?: string | null;
  observed_at?: string | null;
  terminal_at?: string | null;
}

export interface IoTAlert {
  id: string;
  kind: string;
  severity: string;
  message: string;
  status: string;
  node_name?: string;
  created_at: string;
}

export interface DashboardStats {
  nodes_total: number;
  nodes_online: number;
  valves_open: number;
  open_alerts: number;
  commands_total: number;
  commands_success: number;
}

export interface TelemetrySummary {
  sample_count: number;
  total_volume_l: number;
  avg_flow_lpm: number;
  max_flow_lpm: number;
  valve_open_minutes: number;
  battery_min_v: number | null;
  battery_max_v: number | null;
  window_hours?: number;
}

// ---------------------------------------------------------------------------
// Field-commissioning bindings (backend authority for parcel → valve channel)
// ---------------------------------------------------------------------------

export interface ParcelValveBinding {
  binding_id: string;
  site: string;
  sector_id: string;
  sector_code: string;
  parcel: string;
  controller_node_id: string;
  channel_id: string;
  binding_status: string;
  commissioning_status: string;
  actuation_status: string;
  physical_installation_confirmed: boolean;
  wiring_confirmed: boolean;
  local_test_ok: boolean;
  remote_test_ok: boolean;
  command_authorized: boolean;
  lock_reason: string | null;
}

export interface PumpBindingView {
  site: string;
  function: string;
  pump_id: string;
  relay_node_id: string;
  relay_channel: string | null;
  relay_channel_status: string;
  interface_type: string | null;
  interface_type_status: string | null;
  direct_motor_switching: boolean;
  hydraulic_group_id: string;
  binding_status: string;
  commissioning_status: string;
  actuation_status: string;
  physical_installation_confirmed: boolean;
  wiring_confirmed: boolean;
  local_test_ok: boolean;
  remote_test_ok: boolean;
  startup_permissive: {
    mode: string;
    operator_confirmation_required: boolean;
    status?: string | null;
  };
  protected_valve_bindings: string[];
  command_authorized: boolean;
}

export interface FieldBindings {
  schema: string;
  revision: string;
  farm_id: string;
  pressure_sensor_by_binding?: Record<string, string>;
  parcel_valve_bindings: ParcelValveBinding[];
  pumps: PumpBindingView[];
}
